/**
 * CoverageSection.js
 * Bloco "Cobertura" do drawer de novo agendamento: Particular ou Convênio e,
 * em convênio, a carteirinha da pessoa (CoveragePicker).
 *
 * Só é renderizado quando a empresa tem operadora ativa (quem não atende
 * convênio não vê nada). Padrão inteligente: ao escolher a pessoa, se o último
 * agendamento dela foi por convênio e a carteirinha segue ativa, já vem
 * Convênio + aquela carteirinha; sem pessoa escolhida fica Particular.
 * O estado (billingType, insuranceCardId) é do drawer; aqui só se escolhe.
 */

import React, { useEffect, useRef } from 'react';
import {
  BILLING_PRIVATE, BILLING_INSURANCE, BILLING_TYPE_OPTIONS,
} from '../../utils/billingType';
import usePatientInsurances from './usePatientInsurances';
import CoveragePicker from './CoveragePicker';
import drawerStyles from './AppointmentDrawer.module.css';
import styles from './CoveragePicker.module.css';

/**
 * Carteirinha sugerida: a do último agendamento se ainda ativa, senão a única,
 * senão a primeira. Null sem carteirinha ativa.
 */
export function pickDefaultCard(activeCards, lastCoverage) {
  if (!activeCards.length) return null;
  const lastId = lastCoverage?.patient_insurance_id;
  const last = lastId != null ? activeCards.find(c => String(c.id) === String(lastId)) : null;
  return last || activeCards[0];
}

/**
 * @param {Object} props
 * @param {number|string|null} props.patientId - pessoa escolhida (null = só Particular)
 * @param {Array} props.providers - CompanyConfig.InsuranceProviders
 * @param {string} props.billingType - 'private' | 'insurance'
 * @param {number|string|null} props.insuranceCardId
 * @param {Function} props.onChange - ({ billingType, insuranceCardId }) => void
 * @param {Function} [props.onProviderChange] - operadora da carteirinha escolhida (id ou null)
 * @param {string} [props.error] - validação "Escolha ou cadastre a carteirinha"
 * @param {boolean} [props.disabled]
 */
export default function CoverageSection({
  patientId,
  providers = [],
  billingType,
  insuranceCardId,
  onChange,
  onProviderChange,
  error,
  disabled = false,
}) {
  const { activeCards, lastCoverage, loading, loadedKey, addCard } = usePatientInsurances(patientId, Boolean(patientId));
  const isInsurance = billingType === BILLING_INSURANCE;

  // Padrão inteligente, uma vez por carga das carteirinhas da pessoa
  const appliedKeyRef = useRef(null);
  useEffect(() => {
    if (!loadedKey || appliedKeyRef.current === loadedKey) return;
    appliedKeyRef.current = loadedKey;
    const suggested = pickDefaultCard(activeCards, lastCoverage);
    if (lastCoverage?.billing_type === BILLING_INSURANCE && suggested
      && String(suggested.id) === String(lastCoverage.patient_insurance_id)) {
      onChange?.({ billingType: BILLING_INSURANCE, insuranceCardId: suggested.id });
    } else if (isInsurance && insuranceCardId == null && suggested) {
      onChange?.({ billingType: BILLING_INSURANCE, insuranceCardId: suggested.id });
    }
  }, [loadedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Operadora da carteirinha: a tabela de procedimentos dela mostra código e valor
  const providerId = isInsurance
    ? (activeCards.find(card => String(card.id) === String(insuranceCardId))?.provider_id ?? null)
    : null;
  useEffect(() => {
    onProviderChange?.(providerId != null ? String(providerId) : null);
  }, [providerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTypeChange = (type) => {
    if (disabled || type === billingType) return;
    if (type === BILLING_PRIVATE) {
      onChange?.({ billingType: BILLING_PRIVATE, insuranceCardId: null });
      return;
    }
    const suggested = pickDefaultCard(activeCards, lastCoverage);
    onChange?.({ billingType: BILLING_INSURANCE, insuranceCardId: suggested ? suggested.id : null });
  };

  const handleCardChange = (cardId) => {
    onChange?.({ billingType: BILLING_INSURANCE, insuranceCardId: cardId });
  };

  const canUseInsurance = Boolean(patientId);

  return (
    <div className={drawerStyles.formSection}>
      <label className={drawerStyles.fieldLabel}>Cobertura</label>
      <div className={drawerStyles.modeSelector} role="radiogroup" aria-label="Cobertura">
        {BILLING_TYPE_OPTIONS.map(option => {
          const blocked = option.id === BILLING_INSURANCE && !canUseInsurance;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={billingType === option.id}
              className={`${drawerStyles.modeBtn} ${billingType === option.id ? drawerStyles.modeBtnActive : ''}`}
              onClick={() => handleTypeChange(option.id)}
              disabled={disabled || blocked}
              title={blocked ? 'Escolha a pessoa para agendar por convênio' : undefined}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {!canUseInsurance && (
        <span className={styles.hint}>Escolha a pessoa para agendar por convênio.</span>
      )}
      {isInsurance && canUseInsurance && (
        <CoveragePicker
          patientId={patientId}
          providers={providers}
          cards={activeCards}
          loading={loading}
          value={insuranceCardId}
          onChange={handleCardChange}
          onCardCreated={addCard}
          error={error}
          disabled={disabled}
        />
      )}
    </div>
  );
}
